/**
 * Time Formatter Utility for Public Live Page
 *
 * Handles timezone-aware formatting of race timestamps.
 * All database timestamps are UTC. We convert to race's local timezone for display.
 */

import { format, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * Format UTC timestamp to race's local time
 *
 * @param utcTimestamp - UTC timestamp string from database (ISO 8601)
 * @param timeZone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @param formatString - date-fns format string (default: 'h:mm a' = '3:21 PM')
 * @returns Formatted time string in race's timezone
 *
 * @example
 * formatTimeInZone('2025-12-05T23:21:18.000Z', 'America/Los_Angeles')
 * // => '3:21 PM'
 */
export function formatTimeInZone(
  utcTimestamp: string,
  timeZone: string,
  formatString: string = 'h:mm a'
): string {
  if (!utcTimestamp) return '';

  // Ensure timestamp has timezone info
  const timestamp = utcTimestamp.endsWith('Z') ||
                   utcTimestamp.includes('+') ||
                   utcTimestamp.includes('-', 10)
    ? utcTimestamp
    : utcTimestamp + 'Z';

  try {
    // Use date-fns-tz to format in target timezone
    return formatInTimeZone(parseISO(timestamp), timeZone, formatString);
  } catch (error) {
    console.error('Error formatting time:', error);
    // Fallback to browser local time
    return format(parseISO(timestamp), formatString);
  }
}

/**
 * Format UTC timestamp to date in race's timezone
 *
 * @example
 * formatDateInZone('2025-12-05T23:21:18.000Z', 'America/Los_Angeles')
 * // => 'Thu, Dec 5'
 */
export function formatDateInZone(
  utcTimestamp: string,
  timeZone: string,
  formatString: string = 'EEE, MMM d'
): string {
  return formatTimeInZone(utcTimestamp, timeZone, formatString);
}

/**
 * Format full timestamp with timezone abbreviation
 *
 * @example
 * formatFullTimestamp('2025-12-05T23:21:18.000Z', 'America/Los_Angeles')
 * // => 'Dec 5, 2025 3:21 PM PST'
 */
export function formatFullTimestamp(
  utcTimestamp: string,
  timeZone: string
): string {
  return formatTimeInZone(utcTimestamp, timeZone, 'MMM d, yyyy h:mm a zzz');
}

/**
 * Convert local race time (without timezone info) to UTC
 * Used for constructing start times from race.date + race.start_time
 *
 * @param localDateTime - Local datetime string (e.g., '2025-12-13T08:00:00')
 * @param timeZone - IANA timezone identifier
 * @returns UTC ISO string
 *
 * @example
 * localToUTC('2025-12-13T08:00:00', 'America/Los_Angeles')
 * // => '2025-12-13T16:00:00.000Z' (8 AM PST = 4 PM UTC)
 */
export function localToUTC(localDateTime: string, timeZone: string): string {
  if (!localDateTime) return '';

  try {
    // AD-233: use fromZonedTime, NOT toZonedTime.
    //
    // The input is a *naive* wall-clock string (e.g. "2026-08-08T04:00:00")
    // that we want to INTERPRET as being in `timeZone` (the race's zone) and
    // convert to the corresponding absolute UTC instant.
    //   - fromZonedTime(naive, tz)  → "this wall time IS in tz; give me the UTC instant"  ✓ what we want
    //   - toZonedTime(naive, tz)    → parses `naive` in the *runtime/browser* zone, so the
    //                                 result silently depends on where the viewer is. That was
    //                                 the bug: a public-live viewer outside the race's zone saw
    //                                 the header START TIME (and any elapsed-clock fallback that
    //                                 relies on this) shifted by their own UTC offset.
    // With fromZonedTime the returned instant is identical for every viewer.
    const utcDate = fromZonedTime(localDateTime, timeZone);
    return utcDate.toISOString();
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    return localDateTime;
  }
}

/**
 * Format a date-only string (YYYY-MM-DD) without timezone conversion
 *
 * This is specifically for race dates that should be displayed as-is,
 * not converted through UTC which causes off-by-one day errors.
 *
 * @param dateString - Date string in YYYY-MM-DD format (e.g., '2026-01-31')
 * @param formatString - date-fns format string (default: 'EEEE, MMMM d, yyyy' = 'Friday, January 31, 2026')
 * @returns Formatted date string without timezone conversion
 *
 * @example
 * formatLocalDate('2026-01-31')
 * // => 'Friday, January 31, 2026' (NOT shifted to Jan 30 due to timezone)
 *
 * @example
 * formatLocalDate('2026-01-31', 'MMM d, yyyy')
 * // => 'Jan 31, 2026'
 */
export function formatLocalDate(
  dateString: string,
  formatString: string = 'EEEE, MMMM d, yyyy'
): string {
  if (!dateString) return '';

  try {
    // Parse YYYY-MM-DD manually to avoid timezone issues
    // new Date('2026-01-31') interprets as UTC, causing off-by-one errors
    const datePart = dateString.split('T')[0];
    if (!datePart) {
      return dateString;
    }

    const parts = datePart.split('-').map(Number);

    // Validate we have all three parts and they're valid numbers
    if (parts.length !== 3 || parts.some(p => isNaN(p))) {
      return dateString;
    }

    // TypeScript-safe access with validation
    const year = parts[0]!;
    const month = parts[1]!;
    const day = parts[2]!;

    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);

    // Format using date-fns
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting local date:', error);
    return dateString;
  }
}
